// Agregación en memoria del informe de respuestas. La vista en pantalla y el
// PDF consumen las MISMAS secciones para que no puedan divergir.

import {
  getAnswer,
  getSelectableQuestions,
  hasRealAnswer,
  parseResponse,
} from "./activityReport";

export function answerKey(studentId, questionId) {
  return `${studentId}|${questionId}`;
}

// Catálogo de preguntas en el orden del selector: las plantillas llegan
// ordenadas por block_order y sort_order, y dentro de cada formulario se
// respeta el orden de config.fields.
export function buildQuestionCatalog(templates) {
  const questions = [];
  const byTemplate = new Map();

  for (const template of templates || []) {
    const templateQuestions = getSelectableQuestions(template);
    if (templateQuestions.length === 0) continue;
    byTemplate.set(template.id, templateQuestions);
    questions.push(...templateQuestions);
  }

  return { questions, byTemplate };
}

// Índice de respuestas no vacías: `${studentId}|${questionId}` -> texto.
export function buildAnswersIndex(progressRows, templatesById, byTemplate) {
  const answers = new Map();

  for (const row of progressRows || []) {
    const template = templatesById.get(row.template_id);
    const questions = byTemplate.get(row.template_id);
    if (!template || !questions) continue;

    const parsed = parseResponse(row.response);
    if (!hasRealAnswer(template, parsed)) continue;

    for (const question of questions) {
      const value = getAnswer(template, question, parsed).trim();
      if (value) answers.set(answerKey(row.student_id, question.questionId), value);
    }
  }

  return answers;
}

// Bloques del selector: agrupa por block_name conservando el orden recibido.
export function buildSelectorBlocks(templates, byTemplate) {
  const blocks = [];
  const byName = new Map();

  for (const template of templates || []) {
    const questions = byTemplate.get(template.id);
    if (!questions) continue;

    const blockName = template.block_name || "Sin bloque";
    if (!byName.has(blockName)) {
      const block = { blockName, activities: [] };
      byName.set(blockName, block);
      blocks.push(block);
    }
    byName.get(blockName).activities.push({ template, questions });
  }

  return blocks;
}

function studentSections(students, questions, answers, hideEmpty) {
  const sections = [];

  for (const student of students || []) {
    const items = [];
    for (const question of questions) {
      const value = answers.get(answerKey(student.id, question.questionId)) || "";
      if (hideEmpty && !value) continue;
      items.push({
        key: question.questionId,
        label: question.label,
        // El label de un campo puede ser ambiguo fuera de su actividad.
        context: question.kind === "form" ? question.templateName : null,
        value,
      });
    }
    if (hideEmpty && items.length === 0) continue;

    sections.push({
      key: student.id,
      title: student.name || student.email || "Alumno sin nombre",
      subtitle: student.email || "",
      items,
    });
  }

  return sections;
}

function questionSections(students, questions, answers, hideEmpty) {
  const sections = [];

  for (const question of questions) {
    const items = [];
    for (const student of students || []) {
      const value = answers.get(answerKey(student.id, question.questionId)) || "";
      if (hideEmpty && !value) continue;
      items.push({
        key: student.id,
        label: student.name || student.email || "Alumno sin nombre",
        context: null,
        value,
      });
    }
    if (hideEmpty && items.length === 0) continue;

    sections.push({
      key: question.questionId,
      title: question.label,
      // En una pregunta única el label ya es el nombre de la actividad.
      subtitle:
        question.kind === "form"
          ? `${question.blockName} · ${question.templateName}`
          : question.blockName,
      items,
    });
  }

  return sections;
}

export function buildReportSections({
  groupBy,
  students,
  selectedQuestions,
  answersIndex,
  hideEmpty,
}) {
  const questions = selectedQuestions || [];
  if (groupBy === "pregunta") {
    return questionSections(students, questions, answersIndex, hideEmpty);
  }
  return studentSections(students, questions, answersIndex, hideEmpty);
}

// "N con respuesta · M sin responder" para la selección actual.
export function countAnsweredStudents(students, selectedQuestions, answersIndex) {
  const list = students || [];
  const questions = selectedQuestions || [];

  const answered = list.filter((student) =>
    questions.some((question) =>
      answersIndex.has(answerKey(student.id, question.questionId))
    )
  ).length;

  return { answered, missing: list.length - answered };
}
